/**
 * Zerops Services - Utility Functions
 *
 * Common functionality shared between zagent and zcde service cards.
 */

import { Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { box, Boxed } from 'ngrx-forms';
import { ProcessActions, ProcessStatuses, Process } from '@zerops/models/process';
import { ServiceStack } from '@zerops/models/service-stack';
import { generateSecurePassword } from '@zerops/zef/core';
import { ZeropsPublicAccess, ZeropsServiceMetadata, ZeropsServiceMetadataMulti, ControlPlaneMode, ZagentMiniConfig, ZagentMiniAgentEntry, ZagentAgentType, ZagentAuthType, AGENT_DISPLAY_NAMES, AGENT_PROVIDER_MAP } from './zerops-services.model';
import { ZAGENT_ENVIRONMENT_KEY, ZCDE_ENVIRONMENT_KEY } from './zerops-services.constant';

// Re-export for convenience
export { generateSecurePassword };

/**
 * Control Plane Service Detection Utilities
 * Centralized detection logic for zagent and zcde services
 */

/**
 * Read a ZCP env var from the service stack — checks the lightweight
 * stack `userData` first, then falls back to `activeAppVersion.userDataList`
 * (which carries secret-typed vars).
 */
const getZcpEnv = (s: ServiceStack, key: string): string | undefined => {
  const fromStack = s.userData?.find((d) => d.key === key)?.content;
  if (fromStack !== undefined && fromStack !== '') return fromStack;

  const versionUserData = (s.activeAppVersion as { userDataList?: Array<{ key: string; content: string }> } | undefined)?.userDataList;
  return versionUserData?.find((d) => d.key === key)?.content;
};

/**
 * Detect whether a `zeropsio/zcp` stack has ANY agent configured or
 * authorized — used by `isZagentService` / `isZcdeService` to flavor the
 * container. We scan the per-agent env keys directly rather than relying
 * on a separate index (the old `ZCP_AGENT_TYPES` CSV was a deploy-time
 * snapshot that never refreshed, so it drifted as soon as the user
 * authorized an off-list agent post-deploy).
 */
const hasAnyAgentEnv = (s: ServiceStack): boolean => {
  return ALL_AGENT_TYPES.some((t) => {
    const suffix = agentTypeToEnvSuffix(t);
    return !!getZcpEnv(s, `ZCP_AGENT_AUTH_TYPE_${suffix}`)
      || getZcpEnv(s, `ZCP_AGENT_OAUTH_${suffix}`) === 'true'
      || !!getZcpEnv(s, `ZCP_AGENT_TOKEN_${suffix}`);
  });
};

/**
 * Check if a service stack is a zagent (AI Agent) service.
 * For the new unified `zeropsio/zcp` recipe, agent presence is determined
 * by ANY per-agent `ZCP_AGENT_*_<SUFFIX>` env — without one, the container
 * is IDE-only.
 */
export const isZagentService = (s: ServiceStack): boolean => {
  if (s.recipeSource === 'zeropsio/zagent') return true;
  if (!s.recipeSource && (s.name === 'zcp' || /^zcp\d+$/.test(s.name))) return true;
  if (s.recipeSource === 'zeropsio/zcp') {
    return hasAnyAgentEnv(s);
  }
  return false;
};

/**
 * Check if a service stack is a zcde (Cloud IDE) service.
 * For the new unified `zeropsio/zcp` recipe, IDE-only means `ZCP_VSCODE=true`
 * with no per-agent env present.
 */
export const isZcdeService = (s: ServiceStack): boolean => {
  if (s.recipeSource === 'zeropsio/zcde') return true;
  if (!s.recipeSource && (s.name === 'zcde' || /^zcde\d+$/.test(s.name))) return true;
  if (s.recipeSource === 'zeropsio/zcp') {
    const hasAgent = hasAnyAgentEnv(s);
    const hasVscode = getZcpEnv(s, 'ZCP_VSCODE') === 'true';
    return !hasAgent && hasVscode;
  }
  return false;
};

/**
 * Check if a service stack is any Control Plane service.
 * The unified `zeropsio/zcp` recipe always counts, even with no flavor flags
 * — routing/listing should treat the container as a control plane regardless.
 */
export const isControlPlaneService = (s: ServiceStack): boolean =>
  s.recipeSource === 'zeropsio/zcp' || isZagentService(s) || isZcdeService(s);

/**
 * Get the Control Plane mode for a service stack
 * Returns null if the service is not a Control Plane service
 */
export const getControlPlaneMode = (s: ServiceStack): ControlPlaneMode | null =>
  isZagentService(s) ? 'ai-agent' : isZcdeService(s) ? 'human' : null;

/**
 * Processing status array - used to determine if service is in transition
 */
export const PROCESSING_STATUSES = [
  'CREATING',
  'DEPLOYING',
  'STARTING',
  'RESTARTING',
  'STOPPING',
  'UPGRADING',
  'RELOADING',
  'REPAIRING'
] as const;

/**
 * Check if service stack is in processing state
 */
export function isServiceProcessing(status: string): boolean {
  return PROCESSING_STATUSES.includes(status as typeof PROCESSING_STATUSES[number]);
}

/**
 * Create process tracking stream for public access operations
 */
export function createPublicAccessLoadingStream(
  allProcesses$: Observable<Process[]>
): Observable<boolean> {
  return allProcesses$.pipe(
    map((processes) => processes?.some((p) =>
      (p.actionName === ProcessActions.stackEnableSubdomainAccess ||
       p.actionName === ProcessActions.stackUpdateUserData ||
       p.actionName === ProcessActions.stackRestart) &&
      (p.status === ProcessStatuses.RUNNING || p.status === ProcessStatuses.PENDING)
    ) ?? false),
    distinctUntilChanged()
  );
}

/**
 * Create build process tracking streams
 */
export function createBuildProcessStreams(allProcesses$: Observable<Process[]>) {
  const runningBuild$ = allProcesses$.pipe(
    map((processes) => processes?.find((p) =>
      p.actionName === ProcessActions.stackBuild &&
      p.status === ProcessStatuses.RUNNING
    ))
  );

  const pendingBuild$ = allProcesses$.pipe(
    map((processes) => processes?.find((p) =>
      p.actionName === ProcessActions.stackBuild &&
      p.status === ProcessStatuses.PENDING
    ))
  );

  return { runningBuild$, pendingBuild$ };
}

/**
 * Extract non-agent-specific service metadata from user data — the VS Code
 * flag and SSHFS mount list. Anything per-agent (auth type, provider,
 * authorized state) lives in `extractServiceMetadataMulti()`.
 */
export function extractServiceMetadata(
  userData: Array<{ key: string; content: string }> | undefined
): ZeropsServiceMetadata {
  if (!userData) return {};
  const get = (key: string) => userData?.find((item) => item.key === key)?.content;

  return {
    hasVscode: get('ZCP_VSCODE') === 'true',
    sshfsHostnames: get('ZCP_SSHFS_HOSTNAMES')?.split(',').map(h => h.trim()).filter(Boolean) ?? []
  };
}

/**
 * Map an agent type to its uppercase env-var suffix.
 * Mirrors the YAML builder's writer convention.
 *
 *   'claude-code'  → 'CLAUDE_CODE'
 *   'codex'        → 'CODEX'
 *   'antigravity'  → 'ANTIGRAVITY'
 *   'opencode-ai'  → 'OPENCODE_AI'
 */
export function agentTypeToEnvSuffix(t: ZagentAgentType): string {
  return t.toUpperCase().replace(/-/g, '_');
}

const ALL_AGENT_TYPES = Object.keys(AGENT_DISPLAY_NAMES) as ZagentAgentType[];

/**
 * Extract multi-agent metadata from user data using the unified
 * `ZCP_AGENT_*_<SUFFIX>` per-agent schema. No agent-index env is read —
 * `agentConfigs` is populated for any agent that has at least one
 * per-agent env present.
 *
 * Per-agent envs consumed:
 *   - `ZCP_AGENT_AUTH_TYPE_<SUFFIX>` → declared mode at deploy
 *   - `ZCP_AGENT_OAUTH_<SUFFIX>`     → "true" once OAuth completes
 *   - `ZCP_AGENT_TOKEN_<SUFFIX>`     → API token value (token mode)
 *
 * `authorized` is derived: an agent is considered authorized if its
 * OAuth flag is `"true"` OR its token is non-empty. Callers don't have
 * to reconcile the two modes themselves.
 */
export function extractServiceMetadataMulti(
  userData: Array<{ key: string; content: string }> | undefined
): ZeropsServiceMetadataMulti {
  const empty: ZeropsServiceMetadataMulti = {
    agentConfigs: {},
    hasVscode: false,
    sshfsHostnames: []
  };
  if (!userData) return empty;
  const get = (key: string) => userData?.find((item) => item.key === key)?.content;

  const agentConfigs: ZeropsServiceMetadataMulti['agentConfigs'] = {};
  for (const t of ALL_AGENT_TYPES) {
    const suffix = agentTypeToEnvSuffix(t);
    const authType = get(`ZCP_AGENT_AUTH_TYPE_${suffix}`) as ZagentAuthType | undefined;
    const oauthDone = get(`ZCP_AGENT_OAUTH_${suffix}`) === 'true';
    const token = get(`ZCP_AGENT_TOKEN_${suffix}`);
    // Skip agents with NO per-agent env at all — they aren't configured
    // and haven't been authed off-list either. Including any of: declared
    // authType, completed OAuth, or a token value marks the agent as
    // present in the metadata.
    if (!authType && !oauthDone && !token) continue;
    agentConfigs[t] = {
      authType,
      authorized: oauthDone || !!token
    };
  }

  return {
    agentConfigs,
    hasVscode: get('ZCP_VSCODE') === 'true',
    sshfsHostnames: get('ZCP_SSHFS_HOSTNAMES')?.split(',').map(h => h.trim()).filter(Boolean) ?? []
  };
}

/**
 * Ids already authorized (per `extractServiceMetadataMulti`'s derived
 * `authorized` flag), out of any agent that has at least one per-agent env
 * present. This is the FE's OWN userData reading — never the container's
 * `embed-ready` announce — so it is available at pick time without waiting
 * on an embed boot (spec-welcome-mode.md §8.1 "rendered immediately from the
 * FE's own userData ... never waited on", applied identically to
 * authorization rather than availability). Consumed by
 * `ZcpOnboardWizardService.pickAgent` (§8.2): picking an already-authorized
 * agent skips `authorizing` and launches immediately.
 */
export function parseAuthorizedAgents(
  userData: Array<{ key: string; content: string }> | undefined
): ZagentAgentType[] {
  const { agentConfigs } = extractServiceMetadataMulti(userData);
  return (Object.keys(agentConfigs) as ZagentAgentType[]).filter(
    (id) => agentConfigs[id]?.authorized
  );
}

/**
 * Detect public access configuration from service stack and user data
 */
export function detectPublicAccess(
  serviceStack: ServiceStack,
  userData: Array<{ key: string; content: string }>
): ZeropsPublicAccess {
  const hasPublicAccess = serviceStack.hasPublicHttpRoutingAccess || serviceStack.hasPublicPortRoutingAccess;
  const subdomain = userData.find(v => v.key === 'zeropsSubdomain')?.content;
  const password = userData.find(v => v.key === 'VSCODE_PASSWORD')?.content;

  // Auth-enabled iff a password was provisioned — code-server without auth simply
  // has no VSCODE_PASSWORD entry.
  if (hasPublicAccess && subdomain && password) {
    return {
      mode: 'public',
      url: subdomain,
      subdomain,
      authEnabled: true,
      password
    };
  }

  if (hasPublicAccess && subdomain) {
    return {
      mode: 'public',
      url: subdomain,
      subdomain,
      authEnabled: false
    };
  }

  return { mode: 'vpn' };
}

/**
 * Form value shape for Control Plane dialog (subset needed for extraction)
 * Defined here to avoid circular dependency with control-plane-dialog module.
 *
 * Per-agent fields are optional for back-compat with callers that may not
 * carry them yet — when absent the resulting `ZagentMiniConfig.agents` is
 * empty and the mini-card falls back to single-agent rendering off the
 * legacy mirror fields (`agentType`/`authType`).
 */
export interface ControlPlaneFormValueForExtraction {
  enableAgent: boolean;
  enableVscode: boolean;
  agentType: ZagentAgentType;
  authType: ZagentAuthType;
  provider: string;
  apiToken: string;
  enablePublicAccess: boolean;
  vscodePassword: string;
  // Per-agent fields from the multi-agent form (Phase 1D). Optional so
  // legacy payloads without them still typecheck.
  enableAgentClaudeCode?: boolean;
  enableAgentCodex?: boolean;
  enableAgentAntigravity?: boolean;
  enableAgentGrok?: boolean;
  enableAgentCursor?: boolean;
  authTypeClaudeCode?: ZagentAuthType;
  authTypeCodex?: ZagentAuthType;
  authTypeAntigravity?: ZagentAuthType;
  authTypeGrok?: ZagentAuthType;
  authTypeCursor?: ZagentAuthType;
  apiTokenClaudeCode?: string;
  apiTokenCodex?: string;
  apiTokenAntigravity?: string;
  apiTokenGrok?: string;
  apiTokenCursor?: string;
  // Per-service selection checkboxes, indexed against the USER-category
  // service list the dialog rendered. Optional for callers without services.
  selectedServices?: boolean[];
}

function getPerAgentApiToken(v: ControlPlaneFormValueForExtraction, t: ZagentAgentType): string | undefined {
  switch (t) {
    case 'claude-code': return v.apiTokenClaudeCode;
    case 'codex': return v.apiTokenCodex;
    case 'antigravity': return v.apiTokenAntigravity;
    case 'grok': return v.apiTokenGrok;
    case 'cursor': return v.apiTokenCursor;
    default: return undefined;
  }
}

/**
 * Reduce the per-agent form fields to the enabled-agent list in canonical
 * order (claude-code → codex → antigravity → grok → cursor). The single source of
 * truth for "which agents did the user enable" — exported so recipe-list
 * and recipe-detail builders derive the same list instead of re-implementing
 * (and drifting from) it inline.
 */
export function deriveAgentsList(v: ControlPlaneFormValueForExtraction): ZagentMiniAgentEntry[] {
  const list: ZagentMiniAgentEntry[] = [];
  if (v.enableAgentClaudeCode) {
    const authType = v.authTypeClaudeCode ?? 'oauth';
    list.push({
      agentType: 'claude-code',
      authType,
      apiToken: authType === 'token' ? v.apiTokenClaudeCode : undefined
    });
  }
  if (v.enableAgentCodex) {
    const authType = v.authTypeCodex ?? 'oauth';
    list.push({
      agentType: 'codex',
      authType,
      apiToken: authType === 'token' ? v.apiTokenCodex : undefined
    });
  }
  if (v.enableAgentAntigravity) {
    const authType = v.authTypeAntigravity ?? 'oauth';
    list.push({
      agentType: 'antigravity',
      authType,
      apiToken: authType === 'token' ? v.apiTokenAntigravity : undefined
    });
  }
  if (v.enableAgentGrok) {
    const authType = v.authTypeGrok ?? 'oauth';
    list.push({
      agentType: 'grok',
      authType,
      apiToken: authType === 'token' ? v.apiTokenGrok : undefined
    });
  }
  if (v.enableAgentCursor) {
    const authType = v.authTypeCursor ?? 'oauth';
    list.push({
      agentType: 'cursor',
      authType,
      apiToken: authType === 'token' ? v.apiTokenCursor : undefined
    });
  }
  return list;
}

/**
 * Extract ZagentMiniConfig from Control Plane dialog form value
 * Used when dialog closes to persist config to ProjectInfoForm
 * Returns a boxed value for ngrx-forms compatibility
 *
 * Primary single-agent fields (`agentType`/`authType`/`provider`/`apiToken`)
 * are derived from the per-agent fields rather than read from form mirror
 * state — that mirror was a per-keystroke setValue chain in the dialog
 * feature and got removed for perf. When per-agent fields are absent
 * (older form payloads), falls back to the legacy mirror fields verbatim.
 *
 * `serviceNames` must be the SAME filtered USER-category service list (in the
 * same order) the dialog rendered its checkboxes against — the form's
 * `selectedServices` boolean array is indexed against it. Omit for callers
 * whose dialog had no services (blank-project flows).
 */
export function extractZagentConfigFromForm(
  formValue: ControlPlaneFormValueForExtraction,
  serviceNames: string[] = []
): Boxed<ZagentMiniConfig> {
  const derived = deriveAgentsList(formValue);
  const agents = derived.length > 0
    ? derived
    : (formValue.enableAgent && formValue.agentType
      ? [ {
        agentType: formValue.agentType,
        authType: formValue.authType,
        apiToken: formValue.authType === 'token' ? formValue.apiToken : undefined
      } ]
      : []);

  const primary = agents[0];

  return box({
    mode: formValue.enableAgent ? 'ai-agent' : 'human',
    enableAgent: formValue.enableAgent,
    enableVscode: formValue.enableVscode,
    agentType: primary?.agentType ?? formValue.agentType,
    authType: primary?.authType ?? formValue.authType,
    provider: primary ? AGENT_PROVIDER_MAP[primary.agentType] : formValue.provider,
    apiToken: primary?.authType === 'token'
      ? (primary.apiToken ?? getPerAgentApiToken(formValue, primary.agentType))
      : undefined,
    agents,
    includeVscode: formValue.enableVscode,
    enablePublicAccess: formValue.enablePublicAccess,
    vscodePassword: formValue.enablePublicAccess && formValue.enableVscode
      ? formValue.vscodePassword
      : undefined,
    selectedServices: (formValue.selectedServices ?? [])
      .map((isSelected, index) => (isSelected ? serviceNames[index] : undefined))
      .filter((name): name is string => !!name)
  });
}

/**
 * Map a legacy `mode` value to the new `{ enableAgent, enableVscode }` defaults shape.
 * - `'human'`           → Cloud IDE only
 * - `'ai-agent'` / undef → both Coding Agent + Cloud IDE on
 */
export function modeToDefaults(mode?: ControlPlaneMode): { enableAgent: boolean; enableVscode: boolean } {
  if (mode === 'human') return { enableAgent: false, enableVscode: true };
  return { enableAgent: true, enableVscode: true };
}

/**
 * Environment Filtering Utilities
 * Centralized filtering for Control Plane environments based on feature flags
 */

/**
 * Check if a feature flag is enabled
 * Feature flags from environment are typically strings: 'true' | 'false' | undefined
 * @param flag - The feature flag value from environment config
 * @returns true if flag is explicitly set to 'true', false otherwise
 * @default false (features shown) when flag is undefined or any other value
 */
export function isFeatureFlagEnabled(flag: string | undefined): boolean {
  return flag === 'true';
}

/**
 * Filter Control Plane environments (AI Agent and Remote CDE) from environment list
 * Used to conditionally show/hide Control Plane environments based on enableControlPlane feature flag
 * @param environments - Array of environments to filter (any type with a key property)
 * @param shouldHideControlPlane - Flag indicating whether to hide Control Plane environments
 *                                  (typically !enableControlPlane from component)
 * @returns Filtered array of environments (original array if flag is false)
 */
export function filterControlPlaneEnvironments<T extends { key: string }>(
  environments: readonly T[],
  shouldHideControlPlane: boolean
): T[] {
  if (!shouldHideControlPlane) return [ ...environments ];

  return environments.filter((env) =>
    env.key !== ZAGENT_ENVIRONMENT_KEY &&
    env.key !== ZCDE_ENVIRONMENT_KEY
  );
}
